import React, { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import axios from '@/utils/axiosConfig';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Shield, Calendar as CalendarIcon, Search, Filter, X, Trash2, CheckSquare, Square } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const AuditLogs = () => {
  const { user, token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedLogs, setSelectedLogs] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // Protection contre les mises à jour sur composant démonté
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchTerm, dateRange]);

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API}/audit-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (isMountedRef.current) setLogs(response.data);
    } catch (error) {
      if (isMountedRef.current) toast.error('Erreur lors du chargement des logs');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const filterLogs = () => {
    let filtered = [...logs];

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.resource_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by date range
    if (dateRange[0] && dateRange[1]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(dateRange[1]);
      endDate.setHours(23, 59, 59, 999);

      filtered = filtered.filter(log => {
        const logDate = new Date(log.timestamp);
        return logDate >= startDate && logDate <= endDate;
      });
    }

    setFilteredLogs(filtered);
  };

  const handleDeleteLog = async (logId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette entrée d\'audit ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/audit-logs/${logId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entrée d\'audit supprimée');
      fetchAuditLogs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDateRangeChange = (value) => {
    setDateRange(value);
    setShowCalendar(false);
  };

  const clearDateFilter = () => {
    setDateRange([null, null]);
  };

  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedLogs([]);
  };

  const toggleSelectLog = (logId) => {
    if (selectedLogs.includes(logId)) {
      setSelectedLogs(selectedLogs.filter(id => id !== logId));
    } else {
      setSelectedLogs([...selectedLogs, logId]);
    }
  };

  const selectAllLogs = () => {
    if (selectedLogs.length === filteredLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(filteredLogs.map(log => log.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedLogs.length === 0) {
      toast.error('Aucune entrée sélectionnée');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedLogs.length} entrée(s) d'audit ?`)) {
      return;
    }

    try {
      await axios.post(`${API}/audit-logs/delete-multiple`, selectedLogs, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${selectedLogs.length} entrée(s) supprimée(s)`);
      setSelectedLogs([]);
      setSelectionMode(false);
      fetchAuditLogs();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      'FILE_CREATED': 'bg-green-100 text-green-800',
      'FILE_UPDATED': 'bg-blue-100 text-blue-800',
      'FILE_DELETED': 'bg-red-100 text-red-800',
      'DOCUMENT_UPLOADED': 'bg-purple-100 text-purple-800',
      'USER_CREATED': 'bg-green-100 text-green-800',
      'USER_UPDATED': 'bg-blue-100 text-blue-800',
      'USER_DELETED': 'bg-red-100 text-red-800',
      'QUOTE_CREATED': 'bg-yellow-100 text-yellow-800',
      'INVOICE_CREATED': 'bg-orange-100 text-orange-800',
      'LOGIN': 'bg-gray-100 text-gray-800'
    };

    return (
      <Badge className={colors[action] || 'bg-gray-100 text-gray-800'}>
        {action}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="audit-container">
        <div className="flex justify-between items-center flex-wrap gap-4">
          <div>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-2">
              <Shield className="inline mr-3 h-10 w-10 text-red-600" />
              Journal d'Audit
            </h1>
            <p className="text-lg text-gray-600">
              {filteredLogs.length} entrées
              {selectionMode && ` - ${selectedLogs.length} sélectionnée(s)`}
            </p>
          </div>
          <div className="flex gap-2">
            {!selectionMode ? (
              <Button
                onClick={toggleSelectionMode}
                variant="outline"
                className="flex items-center gap-2"
              >
                <CheckSquare className="h-5 w-5" />
                Sélectionner
              </Button>
            ) : (
              <>
                <Button
                  onClick={selectAllLogs}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  {selectedLogs.length === filteredLogs.length ? (
                    <>
                      <Square className="h-5 w-5" />
                      Tout désélectionner
                    </>
                  ) : (
                    <>
                      <CheckSquare className="h-5 w-5" />
                      Tout sélectionner
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleDeleteSelected}
                  variant="destructive"
                  disabled={selectedLogs.length === 0}
                  className="flex items-center gap-2"
                >
                  <Trash2 className="h-5 w-5" />
                  Supprimer ({selectedLogs.length})
                </Button>
                <Button
                  onClick={toggleSelectionMode}
                  variant="outline"
                >
                  <X className="h-5 w-5" />
                  Annuler
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-xl">
          <CardContent className="p-6">
            <div className="flex flex-wrap gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <Input
                    placeholder="Rechercher par action, utilisateur..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Calendar Filter */}
              <div className="relative">
                <Button
                  variant="outline"
                  onClick={() => setShowCalendar(!showCalendar)}
                  className="flex items-center gap-2"
                >
                  <CalendarIcon className="h-5 w-5" />
                  {dateRange[0] && dateRange[1]
                    ? `${format(dateRange[0], 'dd/MM/yyyy')} - ${format(dateRange[1], 'dd/MM/yyyy')}`
                    : 'Sélectionner une période'}
                </Button>

                {dateRange[0] && dateRange[1] && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearDateFilter}
                    className="ml-2"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}

                {showCalendar && (
                  <div className="absolute top-full mt-2 z-50 bg-white shadow-2xl rounded-lg p-4 border">
                    <Calendar
                      selectRange={true}
                      onChange={handleDateRangeChange}
                      value={dateRange}
                      locale="fr-FR"
                      className="rounded-lg border-0"
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Logs List */}
        <div className="space-y-4">
          {filteredLogs.map((log, index) => (
            <Card 
              key={index} 
              className={`border-0 shadow-lg hover:shadow-xl transition-all ${
                selectedLogs.includes(log.id) ? 'ring-2 ring-red-500 bg-red-50' : ''
              }`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  {/* Checkbox in selection mode */}
                  {selectionMode && (
                    <div className="pt-1">
                      <input
                        type="checkbox"
                        checked={selectedLogs.includes(log.id)}
                        onChange={() => toggleSelectLog(log.id)}
                        className="w-5 h-5 text-red-600 border-gray-300 rounded focus:ring-red-500 cursor-pointer"
                      />
                    </div>
                  )}
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getActionBadge(log.action)}
                      <span className="text-sm text-gray-500">
                        {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: fr })}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <p className="text-gray-900">
                        <span className="font-semibold">Utilisateur:</span> {log.user_name || 'Système'}
                      </p>
                      {log.resource_type && (
                        <p className="text-gray-700">
                          <span className="font-semibold">Ressource:</span> {log.resource_type}
                        </p>
                      )}
                      {log.resource_id && (
                        <p className="text-gray-600 text-sm">
                          ID: {log.resource_id}
                        </p>
                      )}
                      {log.details && Object.keys(log.details).length > 0 && (
                        <p className="text-gray-600 text-sm mt-2">
                          <span className="font-semibold">Détails:</span> {JSON.stringify(log.details, null, 2)}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Delete button - hidden in selection mode */}
                  {!selectionMode && (
                    <div className="ml-4">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteLog(log.id)}
                        title="Supprimer cette entrée"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {filteredLogs.length === 0 && (
            <Card className="border-0 shadow-xl">
              <CardContent className="p-12 text-center text-gray-500">
                <Shield className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Aucune entrée d'audit trouvée</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AuditLogs;
